﻿import { LiveDataSetStation, LiveDataSetValue, Status } from "../API/LiveData/LiveDataSet";
import { DefaultParameterDefinitions } from "../DefaultParameterDefinitions";
import { MainSite } from "../MainSite";
import { GetWarningThreshold, WarningGroup } from "../Model/WarningGroup";
import { ParameterGroupInfo } from "../ParameterGroupInfoSet";
import { IStationValueFormatter, IStationValueObject } from "./IStationValueFormatter";

declare var sprintf;

export class CurrentVariationFormatter implements IStationValueFormatter {

    constructor(protected parameterGroupSpeed: ParameterGroupInfo, protected parameterGroupDirection: ParameterGroupInfo) {
    }

    public getShortName(): string {
        // Will be used for PopupChartable
        return this.parameterGroupSpeed.ShortName;
    }

    public getUnits(): string {
        // Will be used for PopupChartable
        return MainSite.getUnitConverter().getConvertedUnits(this.parameterGroupSpeed.Units, this.parameterGroupSpeed.Id);
    }

    public getParameterGroupInfo(): ParameterGroupInfo {
        // Will be used for PopupChartable
        return this.parameterGroupSpeed;
    }

    public getDescription(): string {
        return "Current Variation.";
    }

    public getId(): string {
        // Will be used for PopupChartable
        return this.parameterGroupSpeed.Id;
    }

    public getSensorId(): string | undefined {
        return undefined;
    }

    public plottable(): boolean {
        return true;
    }

    public axisMin(): number {
        // Will be used for PopupChartable
        if (typeof this.parameterGroupSpeed.AxisMin == 'undefined') return null;

        return this.parameterGroupSpeed.AxisMin;
    }

    public axisMax(): number {
        // Will be used for PopupChartable
        if (typeof this.parameterGroupSpeed.AxisMax == 'undefined') return null;

        return this.parameterGroupSpeed.AxisMax;
    }

    public plotLines(): boolean {
        if (typeof this.parameterGroupSpeed.PlotLine == 'undefined') return true;

        return this.parameterGroupSpeed.PlotLine;
    }

    public format(liveDataSetStation: LiveDataSetStation, warningRules?: WarningGroup): IStationValueObject {

        // the default if parameter has never existed
        var valueObject: IStationValueObject = {
            Value: '---',
            Status: null,
            WarningThreshold: null,
            ParameterDefinition: null,
            Timestamp: null,
            Sensor: null
        }

        if (liveDataSetStation?.GroupValues && this.parameterGroupSpeed.Id in liveDataSetStation.GroupValues && this.parameterGroupDirection.Id in liveDataSetStation.GroupValues) {

            var speed: LiveDataSetValue = liveDataSetStation.GroupValues[this.parameterGroupSpeed.Id][0];
            var direction: LiveDataSetValue = liveDataSetStation.GroupValues[this.parameterGroupDirection.Id][0];

            var worstStatus: Status = Math.max(speed.ExpiryStatus, direction.ExpiryStatus);

            var paramDef = MainSite.instance.staticDataManager.Schema.ParameterSchema.Stations[liveDataSetStation.Station.Id]
                .Sensors[speed.Sensor]
                .Parameters[speed.Parameter]
                .Variants[0]
                .ParameterDefinition;

            if ((!paramDef || paramDef.Default) && speed.Parameter in DefaultParameterDefinitions.Parameters) {
                paramDef = DefaultParameterDefinitions.Parameters[speed.Parameter];
            }

            // the default if parameter exists but has expired
            valueObject = {
                Value: '///',
                Status: worstStatus,
                WarningThreshold: null,
                ParameterDefinition: paramDef,
                Timestamp: speed.Timestamp > direction.Timestamp ? speed.Timestamp : direction.Timestamp,
                Sensor: speed.Timestamp > direction.Timestamp ? speed.Sensor : direction.Sensor
            }

            if (speed.Value?.ExpiryStatus == Status.Expired || direction.Value?.ExpiryStatus == Status.Expired) {
                return valueObject; // leave value as "///" if expired
            }

            if (typeof speed.Value == 'number' && typeof direction.Value == 'number' && (worstStatus == Status.Good || worstStatus == Status.Expiring)) {

                //// Convert speed according to units
                let speedString: string;

                let unitsConversionSet = MainSite.getUnitConverter().getConversionSet(this.parameterGroupSpeed.Units, this.parameterGroupSpeed.Id);

                if (this.parameterGroupSpeed.Format) {

                    if (unitsConversionSet) {
                        speedString = sprintf(this.parameterGroupSpeed.Format, unitsConversionSet.convertValue(speed.Value));
                    } else {
                        speedString = sprintf(this.parameterGroupSpeed.Format, speed.Value);
                    }

                    if (warningRules) {
                        valueObject.WarningThreshold = GetWarningThreshold(warningRules, speed.Value, this.parameterGroupSpeed, liveDataSetStation);
                    }
                } else {
                    speedString = (MainSite.getUnitConverter().convert(speed.Value, this.parameterGroupSpeed.Units, this.parameterGroupSpeed.Id)).toString();
                }


                //// Convert direction from degrees to cardinal/inter-cardinal notation
                const degreeRange = 45;

                let directionString: string;

                if (direction.Value > (360 - degreeRange / 2) || direction.Value <= (degreeRange / 2 + degreeRange * 0)) {
                    directionString = "N"
                } else if (direction.Value > (degreeRange / 2 + degreeRange * 0) && direction.Value <= (degreeRange / 2 + degreeRange * 1)) {
                    directionString = "NE"
                } else if (direction.Value > (degreeRange / 2 + degreeRange * 1) && direction.Value <= (degreeRange / 2 + degreeRange * 2)) {
                    directionString = "E"
                } else if (direction.Value > (degreeRange / 2 + degreeRange * 2) && direction.Value <= (degreeRange / 2 + degreeRange * 3)) {
                    directionString = "SE"
                } else if (direction.Value > (degreeRange / 2 + degreeRange * 3) && direction.Value <= (degreeRange / 2 + degreeRange * 4)) {
                    directionString = "S"
                } else if (direction.Value > (degreeRange / 2 + degreeRange * 4) && direction.Value <= (degreeRange / 2 + degreeRange * 5)) {
                    directionString = "SW"
                } else if (direction.Value > (degreeRange / 2 + degreeRange * 5) && direction.Value <= (degreeRange / 2 + degreeRange * 6)) {
                    directionString = "W"
                } else if (direction.Value > (degreeRange / 2 + degreeRange * 6) && direction.Value <= (degreeRange / 2 + degreeRange * 7)) {
                    directionString = "NW"
                }

                //// Set valueObject.Value
                valueObject.Value = `${speedString} ${directionString}`;
            }
        }

        return valueObject;
    }
}
